/**
 * Audit OO1 — abgeleiteter Management-Status für offene Positionen.
 *
 * Liest existierende Position-Felder (current_price, stops, take_profits,
 * tp_hits, current_r_multiple) und berechnet:
 *   - Distanz zum Stop in %
 *   - nächster aktiver Take-Profit + Distanz in %
 *   - Trade-Progress (% von Entry zum nächsten TP, clamped 0-100)
 *   - empfohlene next_action (hold / watch / stop_near / tp_near / partial_take / exit_check)
 *   - kurze Klartext-Hinweise
 *
 * Long-Only — Short kommt mit IBKR-Bridge.
 */

import type { Position } from '@/types';

export type PositionAction =
  | 'hold'
  | 'watch'
  | 'stop_near'
  | 'tp_near'
  | 'partial_take'
  | 'exit_check'
  | 'trade_advanced';

export interface PositionMgmt {
  /** Distanz vom aktuellen Kurs zum Stop in % (negativ = unter Stop). */
  distance_to_stop_pct: number | null;
  /** Label des nächsten ungehitteten TP, z.B. "TP1". null wenn alle hit. */
  next_tp_label: 'TP1' | 'TP2' | 'TP3' | null;
  /** Preis des nächsten TP. */
  next_tp_price: number | null;
  /** Distanz vom Kurs zum nächsten TP in %. */
  distance_to_next_tp_pct: number | null;
  /** Wie weit ist der Trade von Entry zum nächsten TP gelaufen? 0–100 %. */
  trade_progress_pct: number | null;
  /** Wurde der Stop schon nachgezogen über initial? */
  stop_moved_to_breakeven: boolean;
  stop_above_initial: boolean;
  /** SS1: Initial-Stop-Distanz Entry → Stop in %. > 15 % gilt als "zu weit". */
  initial_stop_distance_pct: number | null;
  /** SS1: True, wenn der Initial-Stop >= 15 % unter Entry war (unrealistisch). */
  stop_too_wide: boolean;
  /** Aggregierter Vorschlag. */
  next_action: PositionAction;
  /** Kurze Klartext-Hinweise, max 3, sortiert nach Wichtigkeit. */
  hints: string[];
}

/** Long-Bewertung. Returns null wenn Position-Daten unvollständig sind.
 * @param currentScore Aktueller Setup-Score (0–100), optional. Wenn vorhanden,
 *                     wird er gegen den entry_score_snapshot der Position
 *                     verglichen → ergibt „Score seit Einstieg verschlechtert"-Hint.
 */
export function derivePositionMgmt(
  position: Position,
  currentScore?: number | null
): PositionMgmt | null {
  const cur = position.current_price;
  if (cur === null || !Number.isFinite(cur) || cur <= 0) return null;
  const entry = position.avg_entry_price;
  const stop = position.current_stop;

  // Distanz zum Stop (Long: positive Zahl heißt Kurs > Stop = sicher)
  const distStop = cur > 0 ? ((cur - stop) / cur) * 100 : null;

  // Nächster aktiver TP — der erste nicht gehittete und nicht-null
  let nextTpLabel: 'TP1' | 'TP2' | 'TP3' | null = null;
  let nextTpPrice: number | null = null;
  if (position.take_profit_1 !== null && !position.tp1_hit) {
    nextTpLabel = 'TP1';
    nextTpPrice = position.take_profit_1;
  } else if (position.take_profit_2 !== null && !position.tp2_hit) {
    nextTpLabel = 'TP2';
    nextTpPrice = position.take_profit_2;
  } else if (position.take_profit_3 !== null && !position.tp3_hit) {
    nextTpLabel = 'TP3';
    nextTpPrice = position.take_profit_3;
  }

  const distTp =
    nextTpPrice !== null && cur > 0 ? ((nextTpPrice - cur) / cur) * 100 : null;

  // Trade-Progress: 0 = bei Entry, 100 = beim nächsten TP, >100 = über TP
  let progress: number | null = null;
  if (nextTpPrice !== null && nextTpPrice > entry) {
    const raw = ((cur - entry) / (nextTpPrice - entry)) * 100;
    progress = Math.max(0, Math.min(100, raw));
  } else if (position.tp3_hit && position.take_profit_3 !== null) {
    progress = 100;
  }

  const stopAboveInit = stop > position.initial_stop + 0.0001;
  const stopAtBE =
    Math.abs(stop - entry) / Math.max(entry, 0.01) < 0.005 && stopAboveInit;

  // SS1: Initial-Stop-Distanz prüfen (auf Plan-Basis, nicht aktuelle Stop-Position)
  const initStopDist =
    entry > 0
      ? ((entry - position.initial_stop) / entry) * 100
      : null;
  const stopTooWide = initStopDist !== null && initStopDist > 15;

  const r = position.current_r_multiple;

  // Action-Logik
  const hints: string[] = [];
  let action: PositionAction = 'hold';

  if (distStop !== null && distStop < 0) {
    action = 'exit_check';
    hints.push(
      `Kurs ${Math.abs(distStop).toFixed(1)} % unter Stop — Position müsste eigentlich geschlossen sein.`
    );
  } else if (distStop !== null && distStop < 1.5) {
    action = 'stop_near';
    hints.push(`Nur ${distStop.toFixed(1)} % bis Stop — Stop im Blick.`);
  } else if (r !== null && r < -0.7) {
    action = 'exit_check';
    hints.push(
      `R aktuell ${r.toFixed(2)} — Setup hat sich klar entgegen Plan entwickelt, Exit prüfen.`
    );
  } else if (distTp !== null && distTp < 1.5 && nextTpLabel) {
    action = 'tp_near';
    hints.push(`Nur ${distTp.toFixed(1)} % bis ${nextTpLabel} — Teilgewinn prüfen.`);
  } else if (position.tp1_hit && !position.tp2_hit) {
    action = 'partial_take';
    hints.push(
      `${stopAtBE ? 'TP1 erreicht, Stop auf Break-even' : 'TP1 erreicht'} — Rest läuft auf TP2.${stopAtBE ? '' : ' Stop auf BE prüfen.'}`
    );
  } else if (progress !== null && progress > 70) {
    action = 'trade_advanced';
    hints.push(
      `Trade ${progress.toFixed(0)} % zwischen Entry und ${nextTpLabel ?? 'Ziel'} gelaufen — Stop nachziehen prüfen.`
    );
  } else if (r !== null && r >= 1.0 && distStop !== null && distStop > 5) {
    action = 'watch';
    hints.push(`Trade läuft +${r.toFixed(1)} R — Rücksetzer-Risiko nimmt zu, Stop nachziehen.`);
  } else {
    hints.push('Trend intakt — halten.');
  }

  // Zweite Zeile: TP-/Stop-Distanz als Sekundär-Info
  if (nextTpLabel && distTp !== null && action !== 'tp_near') {
    hints.push(
      `Noch ${distTp.toFixed(1)} % bis ${nextTpLabel}${
        distStop !== null ? ` · ${distStop.toFixed(1)} % bis Stop` : ''
      }.`
    );
  }
  if (!nextTpLabel && position.tp3_hit) {
    hints.push('Alle TPs erreicht — Restposition als Runner mit nachgezogenem Stop.');
  }
  if (stopAtBE && action !== 'partial_take') {
    hints.push('Stop auf Break-even — Trade kann nicht mehr in Verlust gehen.');
  }

  // SS1: Stop-zu-weit-Hinweis prominent in Hints aufnehmen
  if (stopTooWide && initStopDist !== null) {
    hints.unshift(
      `Initial-Stop ${initStopDist.toFixed(0)} % unter Entry — sehr weit für einen Swing-Trade. TPs sind dadurch unrealistisch hoch. Manuellen technischen Stop setzen oder Position schließen.`
    );
  }

  // UU2: Score-Verschlechterung seit Einstieg
  const entrySnap = position.entry_score_snapshot;
  if (
    typeof currentScore === 'number' &&
    Number.isFinite(currentScore) &&
    typeof entrySnap === 'number' &&
    Number.isFinite(entrySnap)
  ) {
    const diff = currentScore - entrySnap;
    if (diff <= -10) {
      hints.unshift(
        `Score seit Einstieg ${diff} Punkte — Setup hat sich klar verschlechtert. Stop nachziehen oder Exit prüfen.`
      );
      if (action === 'hold') action = 'watch';
    } else if (diff <= -5) {
      hints.push(
        `Score seit Einstieg ${diff} Punkte — Trade leicht schwächer geworden, beobachten.`
      );
    } else if (diff >= 8) {
      hints.push(
        `Score seit Einstieg +${diff} Punkte — Setup hat an Qualität gewonnen.`
      );
    }
  }

  return {
    distance_to_stop_pct: distStop,
    next_tp_label: nextTpLabel,
    next_tp_price: nextTpPrice,
    distance_to_next_tp_pct: distTp,
    trade_progress_pct: progress,
    stop_moved_to_breakeven: stopAtBE,
    stop_above_initial: stopAboveInit,
    initial_stop_distance_pct: initStopDist,
    stop_too_wide: stopTooWide,
    next_action: action,
    hints: hints.slice(0, 4),
  };
}

/** Farb-Token für Status-Pill. */
export const ACTION_PILL_CLASS: Record<PositionAction, string> = {
  hold: 'bg-zinc-800/60 border-zinc-700/60 text-zinc-300',
  watch: 'bg-blue-950/40 border-blue-800/60 text-blue-200',
  stop_near: 'bg-red-950/40 border-red-800/60 text-red-200',
  tp_near: 'bg-emerald-950/40 border-emerald-800/60 text-emerald-200',
  partial_take: 'bg-emerald-950/30 border-emerald-800/50 text-emerald-200',
  exit_check: 'bg-red-950/50 border-red-700/60 text-red-100',
  trade_advanced: 'bg-amber-950/40 border-amber-800/60 text-amber-200',
};

export const ACTION_LABEL: Record<PositionAction, string> = {
  hold: 'Halten',
  watch: 'Beobachten',
  stop_near: 'Stop im Blick',
  tp_near: 'TP nahe',
  partial_take: 'Teilgewinn / BE',
  exit_check: 'Exit prüfen',
  trade_advanced: 'Stop nachziehen',
};
